/** Brain ▸ Files — web mirror of `Sources/Views/FilesView.swift`.
 *
 * Browse a directory on the operator's own Mac, read through `AppEnvironment.files`
 * (MockFilesStore offline; BrainFilesStore against the Worker's OWNER-ONLY
 * `GET /files?path=` in the live path). Clicking a folder navigates into it
 * (re-drives the load with the new `?path=`); a breadcrumb and an "up" affordance
 * walk back out.
 *
 * The live call dispatches to the Mac bridge and can take up to ~25s or fail
 * (owner-only 403 / engine-offline 502·503·504), so this owns its own load state
 * with a clear "contacting your engine" indicator and a refresh, and is cancelable
 * (a stale in-flight response is ignored). */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useEnv } from '../app/context';
import { brainFsKind, isDirectory, type FileEntry, type FilesListResponse } from '../models/brainFs';
import { EmptyState, Section } from '../components/UIHelpers';

export function FilesView() {
  const env = useEnv();
  const [path, setPath] = useState('~');
  const [listing, setListing] = useState<FilesListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const reqId = useRef(0);

  const load = useCallback(
    async (target: string) => {
      const myReq = ++reqId.current;
      setIsLoading(true);
      setError(null);
      try {
        const result = await env.files.list(target === '~' ? undefined : target);
        if (myReq === reqId.current) setListing(result);
      } catch (e) {
        if (myReq === reqId.current) {
          setError(e);
          setListing(null);
        }
      } finally {
        if (myReq === reqId.current) setIsLoading(false);
      }
    },
    [env],
  );

  useEffect(() => {
    void load(path);
  }, [load, path]);

  const currentPath = listing?.path ?? path;
  const crumbs = breadcrumbs(currentPath);
  const canGoUp = currentPath !== '/' && currentPath !== '~' && currentPath.length > 0;

  const goUp = () => {
    if (!canGoUp) return;
    const parent = currentPath.replace(/\/[^/]*$/, '') || '/';
    setPath(parent);
  };

  const navigate = (to: string) => {
    if (to === path) void load(to);
    else setPath(to);
  };

  return (
    <>
      <div className="fs-pathbar">
        <div className="fs-pathbar__row">
          <span aria-hidden>📁</span>
          <span className="fs-pathbar__path" title={currentPath}>
            {currentPath || '~'}
          </span>
          <button className="fs-btn" onClick={goUp} disabled={!canGoUp || isLoading} title="Up one level">
            ↑ Up
          </button>
          <button className="fs-btn" onClick={() => void load(path)} disabled={isLoading} title="Refresh">
            ⟳
          </button>
        </div>
        {crumbs.length > 1 ? (
          <div className="fs-crumbs">
            {crumbs.map((c, i) => (
              <span key={c.absolute}>
                <button
                  className="fs-crumb"
                  disabled={isLoading || i === crumbs.length - 1}
                  onClick={() => navigate(c.absolute)}
                >
                  {c.label}
                </button>
                {i < crumbs.length - 1 ? <span className="fs-crumb__sep">›</span> : null}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {isLoading && !listing ? (
        <LoadingState />
      ) : error && !listing ? (
        <FilesErrorState error={error} onRetry={() => void load(path)} />
      ) : listing && listing.entries.length === 0 ? (
        <EmptyState icon="📂" title="Empty folder" message="Nothing to show in this directory." />
      ) : listing ? (
        <Section header={`${listing.count} items`}>
          {sortEntries(listing.entries).map((entry) => (
            <FileRow key={entry.path ?? entry.name} entry={entry} onOpen={() => entry.path && navigate(entry.path)} />
          ))}
        </Section>
      ) : null}
      <div style={{ height: 24 }} />
    </>
  );
}

function FileRow({ entry, onOpen }: { entry: FileEntry; onOpen: () => void }) {
  const dir = isDirectory(entry);
  const inner = (
    <>
      <span aria-hidden style={{ width: 22, textAlign: 'center' }}>
        {dir ? '📁' : '📄'}
      </span>
      <div className="row__title" style={{ flex: 1, minWidth: 0, fontSize: 14 }}>
        {entry.name}
      </div>
      {!dir && entry.size != null ? <span className="caption mono">{byteString(entry.size)}</span> : null}
      {dir ? <span className="caption" aria-hidden>›</span> : null}
    </>
  );
  return dir ? (
    <button className="row row--tappable" onClick={onOpen}>
      {inner}
    </button>
  ) : (
    <div className="row">{inner}</div>
  );
}

function LoadingState() {
  return (
    <div className="empty">
      <div className="spinner" />
      <div className="empty__title" style={{ marginTop: 12 }}>
        Contacting your engine…
      </div>
      <div className="caption" style={{ marginTop: 4 }}>
        This can take up to ~25 seconds.
      </div>
    </div>
  );
}

/** Owner-only (403) and engine-offline (502·503·504) get dedicated copy. */
function FilesErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  switch (brainFsKind(error)) {
    case 'ownerOnly':
      return (
        <EmptyState icon="🔒" title="Owner only" message="Files are only available on your own engine (owner only)." />
      );
    case 'engineOffline':
      return (
        <>
          <EmptyState icon="⚡︎" title="Engine offline" message="Engine offline — start your engine to browse files." />
          <div style={{ textAlign: 'center' }}>
            <button className="fs-btn" onClick={onRetry}>
              Try again
            </button>
          </div>
        </>
      );
    case 'notSignedIn':
      return <EmptyState icon="🔑" title="Not signed in" message="Sign in to browse files on your engine." />;
    default:
      return (
        <>
          <EmptyState
            icon="⚠"
            title="Couldn't load files"
            message={error instanceof Error ? error.message : String(error)}
          />
          <div style={{ textAlign: 'center' }}>
            <button className="fs-btn" onClick={onRetry}>
              Retry
            </button>
          </div>
        </>
      );
  }
}

// MARK: - Helpers

function sortEntries(entries: FileEntry[]): FileEntry[] {
  return [...entries].sort((a, b) => {
    const ad = isDirectory(a);
    const bd = isDirectory(b);
    if (ad !== bd) return ad ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

function byteString(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n < 10 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
}

interface Crumb {
  label: string;
  absolute: string;
}

function breadcrumbs(p: string): Crumb[] {
  if (!p.startsWith('/')) return [{ label: p || '~', absolute: p }];
  const parts = p.split('/').filter(Boolean);
  const crumbs: Crumb[] = [{ label: '/', absolute: '/' }];
  let acc = '';
  for (const part of parts) {
    acc += '/' + part;
    crumbs.push({ label: part, absolute: acc });
  }
  return crumbs;
}
